import React, { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Download, BoxArrowUpRight } from "react-bootstrap-icons";
import { BaseWidget, WidgetIconButton } from "../common";
import { buildContractsQuery } from "../../hooks/buildContractsQuery";
import "../../styles/NotationsWidget.css";
import { getCategoryLabel, NOTATION_CATEGORY_THEMES } from "../../constants";

export function NotationsWidget({
  data,
  isLoading,
  error,
  onNavigate,
  onDownload,
}) {
  const categories = data?.categories || [];
  const totalContracts = data?.totalContracts ?? 0;

  return (
    <BaseWidget
      title="Notations by Category"
      subtitle={
        data
          ? `${totalContracts.toLocaleString()} contracts have notations`
          : null
      }
      isLoading={isLoading}
      error={error}
      headerRight={
        <>
          {onDownload && (
            <WidgetIconButton
              icon={<Download />}
              title="Download"
              onClick={onDownload}
            />
          )}
          <WidgetIconButton
            icon={<BoxArrowUpRight />}
            title="View all"
            onClick={() =>
              onNavigate(
                "/contracts?" +
                  buildContractsQuery({ hasNotations: "true" }, {}),
              )
            }
          />
        </>
      }
    >
      <Row className="g-3 cp-notations__row">
        {categories.map((cat, idx) => (
          <Col xs={12} md={6} key={cat.description ?? idx}>
            <CategoryCard
              category={cat}
              theme={
                NOTATION_CATEGORY_THEMES[idx % NOTATION_CATEGORY_THEMES.length]
              }
              onNavigate={onNavigate}
            />
          </Col>
        ))}
      </Row>
    </BaseWidget>
  );
}

// ============================================================
//  One category card — donut + full legend list
// ============================================================
const CategoryCard = ({ category, theme, onNavigate }) => {
  const reasons = category.reasons || [];

  // Donut data — every reason is its own wedge
  const donutData = useMemo(
    () =>
      reasons.map((r, i) => ({
        name: r.text,
        value: r.contractCount,
        color: theme.ramp[i % theme.ramp.length],
      })),
    [reasons, theme],
  );

  const handleReasonClick = (reasonText) => {
    onNavigate(
      "/contracts?" + buildContractsQuery({ notationText: reasonText }, {}),
    );
  };

  return (
    <div
      className="cp-notations-card"
      style={{
        backgroundColor: theme.bg,
        borderLeftColor: theme.accent,
      }}
    >
      <Row className="g-0 align-items-baseline cp-notations-card__header">
        <Col>
          <span
            className="cp-notations-card__title"
            style={{ color: theme.titleColor }}
          >
            {getCategoryLabel(category.description)}
          </span>
        </Col>
      </Row>

      <Row className="g-2 align-items-center cp-notations-card__chart-row">
        <Col xs="auto">
          <div className="cp-notations-card__donut">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  cx="50%"
                  cy="50%"
                  innerRadius="62%"
                  outerRadius="95%"
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                  isAnimationActive={false}
                >
                  {donutData.map((slice, i) => (
                    <Cell key={i} fill={slice.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="cp-notations-card__donut-center" aria-hidden="true">
              <span
                className="cp-notations-card__donut-total"
                style={{ color: theme.titleColor }}
              >
                {category.contractCount}
              </span>
            </div>
          </div>
        </Col>

        <Col className="cp-notations-card__legend-col">
          <ul className="cp-notations-card__legend">
            {reasons.map((r, i) => (
              <li key={r.text}>
                <button
                  type="button"
                  className="cp-notations-card__legend-row"
                  onClick={() => handleReasonClick(r.text)}
                >
                  <Row className="g-0 align-items-center w-100">
                    <Col xs="auto">
                      <span
                        className="cp-notations-card__dot"
                        style={{
                          backgroundColor: theme.ramp[i % theme.ramp.length],
                        }}
                        aria-hidden="true"
                      />
                    </Col>
                    <Col
                      className="ps-2 cp-notations-card__reason-text"
                      style={{ color: theme.titleColor }}
                    >
                      {r.text}
                    </Col>
                    <Col
                      xs="auto"
                      className="cp-notations-card__reason-count"
                      style={{ color: theme.titleColor }}
                    >
                      {r.contractCount}
                    </Col>
                  </Row>
                </button>
              </li>
            ))}
          </ul>
        </Col>
      </Row>
    </div>
  );
};
